import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { FaCheckCircle, FaDownload, FaInfoCircle, FaRedo, FaShieldAlt, FaShoppingCart, FaSyncAlt } from "react-icons/fa";
import SEO from "../components/SEO";
import ProductRating from "../components/ProductRating";
import { resolveKyrosApiUrl } from "../services/apiConfig";
import "../style/StorePage.css";

const AUTH_STORAGE_KEY = "kyros_auth_v1";

const PRODUCT_MEDIA = {
  warfront: {
    hero: "/images/warfront_background.jpg",
    cover: "/images/warfront_background.jpg",
  },
  "zombie-crushers": {
    hero: "/images/zombie_crushers_carousel_2880x960.webp",
    cover: "/images/zombie_crushers_carousel_1600x1400.webp",
  },
  "ghost-hunter": {
    hero: "/images/ghost_hunter_carousel_2880x960.webp",
    cover: "/images/ghost_hunter_carousel_1600x1400.webp",
  },
};

const FALLBACK_PRODUCTS = [
  {
    code: "warfront",
    display_name: "WarFront",
    status: "active",
    installable: true,
    free_claim_enabled: false,
    metadata: {
      publisher: "Kyros Games",
      launcher: { category: "tactical-fps", premium: true },
      store: {
        visibility: "featured",
        purchaseType: "paid",
        provider: "stripe",
        status: "available",
      },
    },
    ownership: { owned: false },
    actions: { owned: false, can_install: false, can_claim: false, can_purchase: true },
  },
  {
    code: "zombie-crushers",
    display_name: "Zombie Crushers",
    status: "active",
    installable: true,
    free_claim_enabled: true,
    metadata: {
      publisher: "Kyros Games",
      launcher: { category: "action", freeClaim: "enabled" },
      store: { visibility: "listed", purchaseType: "free", status: "available" },
    },
    ownership: { owned: false },
    actions: { owned: false, can_install: false, can_claim: true, can_purchase: false },
  },
];

function readStoredAuth() {
  if (typeof window === "undefined") return null;
  try {
    return JSON.parse(window.localStorage.getItem(AUTH_STORAGE_KEY) || "null");
  } catch (_error) {
    return null;
  }
}

function resolveApiUrl(path) {
  return resolveKyrosApiUrl(path);
}

function getStoreMeta(product) {
  const metadata = product && typeof product.metadata === "object" ? product.metadata : {};
  const store = metadata && typeof metadata.store === "object" ? metadata.store : {};
  return store;
}

function getProductMedia(product) {
  return PRODUCT_MEDIA[product.code] || {};
}

function getPurchaseType(product) {
  const store = getStoreMeta(product);
  return String(store.purchaseType || store.purchase_type || "").trim().toLowerCase();
}

function getPriceDisplay(product) {
  const store = getStoreMeta(product);
  return String(product.price_display || product.priceDisplay || store.priceDisplay || store.price_display || "").trim();
}

function getProductStatusLabel(product) {
  const actions = product.actions || {};
  if (actions.owned) return "In libreria";
  if (actions.can_purchase) return "Acquisto digitale";
  if (actions.can_claim) return "Incluso gratis";
  const store = getStoreMeta(product);
  if (String(store.visibility || "").toLowerCase() === "coming_soon") return "In arrivo";
  return "Non disponibile";
}

function getProductRoute(product) {
  if (!product || !product.code) return "/games";
  return `/games/${product.code}`;
}

function normalizeProducts(payload) {
  const products = Array.isArray(payload && payload.products) ? payload.products : [];
  return products.length ? products : FALLBACK_PRODUCTS;
}

export default function StorePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [auth, setAuth] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncState, setSyncState] = useState("loading");
  const [paymentsEnabled, setPaymentsEnabled] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [busyCode, setBusyCode] = useState("");

  const checkoutState = searchParams.get("checkout");
  const activeSession = Boolean(auth && auth.access_token);

  const refreshAuth = useCallback(() => {
    setAuth(readStoredAuth());
  }, []);

  const fetchProducts = useCallback(async () => {
    const currentAuth = readStoredAuth();
    setAuth(currentAuth);
    setLoading(true);
    setError("");

    try {
      const headers = {};
      if (currentAuth && currentAuth.access_token) {
        headers.Authorization = `Bearer ${currentAuth.access_token}`;
      }

      const response = await fetch(resolveApiUrl("/auth/store/products"), { headers });
      if (!response.ok) {
        throw new Error(`store_products_${response.status}`);
      }

      const data = await response.json();
      setProducts(normalizeProducts(data));
      const nextPaymentsEnabled = Boolean(data && data.payments && data.payments.enabled);
      setPaymentsEnabled(nextPaymentsEnabled);
      setSyncState(nextPaymentsEnabled ? "live" : "limited");
    } catch (_error) {
      setProducts(FALLBACK_PRODUCTS);
      setPaymentsEnabled(false);
      setSyncState("fallback");
      setNotice("Non riesco a leggere i dati live dello Store. Ti mostro il catalogo base.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    window.addEventListener("storage", refreshAuth);
    window.addEventListener("focus", refreshAuth);
    window.addEventListener("kyros-auth-updated", refreshAuth);
    return () => {
      window.removeEventListener("storage", refreshAuth);
      window.removeEventListener("focus", refreshAuth);
      window.removeEventListener("kyros-auth-updated", refreshAuth);
    };
  }, [refreshAuth]);

  useEffect(() => {
    if (checkoutState === "success") {
      setNotice("Pagamento completato. La libreria si aggiorna appena arriva la conferma Stripe.");
      fetchProducts();
    } else if (checkoutState === "cancelled") {
      setNotice("Checkout annullato. Nessun addebito completato.");
    }
  }, [checkoutState, fetchProducts]);

  const featuredProduct = useMemo(
    () => products.find((product) => product.code === "warfront") || products[0],
    [products]
  );

  const handleCheckout = async (product) => {
    if (!product) return;
    if (!activeSession) {
      navigate("/account");
      return;
    }

    setBusyCode(product.code);
    setError("");
    setNotice("");

    try {
      const currentAuth = readStoredAuth();
      const response = await fetch(resolveApiUrl("/auth/store/checkout"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${currentAuth.access_token}`,
        },
        body: JSON.stringify({ game_code: product.code }),
      });
      const data = await response.json().catch(() => ({}));

      if (response.status === 401 || response.status === 403) {
        navigate("/account");
        return;
      }

      if (!response.ok) {
        const message =
          data.message ||
          data.error ||
          "Checkout non disponibile in questo momento.";
        throw new Error(message);
      }

      if (data.checkout_url) {
        window.location.assign(data.checkout_url);
        return;
      }

      if (data.mode === "already_owned") {
        setNotice("Questo gioco è già nella tua libreria.");
      } else if (data.mode === "free-claim") {
        setNotice("Gioco aggiunto alla libreria.");
      }
      await fetchProducts();
    } catch (err) {
      setError(err.message || "Errore durante il checkout.");
    } finally {
      setBusyCode("");
    }
  };

  const renderProductAction = (product) => {
    const actions = product.actions || {};
    const isBusy = busyCode === product.code;

    if (actions.owned && !actions.can_purchase_additional_license) {
      return (
        <Link to="/account" className="store-page__secondary-action">
          <FaCheckCircle aria-hidden="true" />
          Libreria
        </Link>
      );
    }

    if (actions.can_claim) {
      return (
        <button
          type="button"
          className="store-page__primary-action"
          disabled={isBusy}
          onClick={() => handleCheckout(product)}
        >
          {isBusy ? <FaSyncAlt aria-hidden="true" /> : <FaDownload aria-hidden="true" />}
          {isBusy ? "Sincronizzo..." : activeSession ? "Aggiungi gratis" : "Accedi e aggiungi"}
        </button>
      );
    }

    if (actions.can_purchase) {
      if (!paymentsEnabled) {
        return (
          <Link to={getProductRoute(product)} className="store-page__secondary-action">
            <FaInfoCircle aria-hidden="true" />
            Vedi scheda prodotto
          </Link>
        );
      }

      return (
        <button
          type="button"
          className="store-page__primary-action"
          disabled={isBusy}
          onClick={() => handleCheckout(product)}
        >
          {isBusy ? <FaSyncAlt aria-hidden="true" /> : <FaShoppingCart aria-hidden="true" />}
          {isBusy
            ? "Apro il checkout..."
            : activeSession
              ? actions.can_purchase_additional_license
                ? "Acquista licenza"
                : `Acquista ${product.display_name}`
              : "Accedi per acquistare"}
        </button>
      );
    }

    return (
      <button type="button" className="store-page__disabled-action" disabled>
        In arrivo
      </button>
    );
  };

  return (
    <section className="store-page">
      <SEO
        title="Kyros Store | Giochi Kyros"
        description="Acquista WarFront o aggiungi giochi gratuiti alla libreria Kyros."
        canonical="https://kyrosgames.com/store"
        image="https://kyrosgames.com/images/warfront_background.jpg"
        imageAlt="WarFront gameplay screenshot"
      />

      <div
        className="store-page__hero"
        style={{
          backgroundImage: featuredProduct
            ? `linear-gradient(90deg, rgba(7, 10, 18, 0.94), rgba(7, 10, 18, 0.62)), url(${getProductMedia(featuredProduct).hero || ""})`
            : undefined,
        }}
      >
        <div className="store-page__hero-content">
          <span className="store-page__eyebrow">Kyros Store</span>
          <h1>Kyros Store</h1>
          <p>
            Acquista WarFront o aggiungi i giochi gratuiti alla libreria. Ogni prodotto indica prima cosa ricevi, dove lo installi e come chiedere supporto.
          </p>
          {featuredProduct ? (
            <ProductRating
              productCode={featuredProduct.code}
              productName={featuredProduct.display_name}
              rating={featuredProduct}
            />
          ) : null}
          <div className="store-page__hero-actions">
            {featuredProduct && renderProductAction(featuredProduct)}
            {featuredProduct && (
              <Link to={getProductRoute(featuredProduct)} className="store-page__ghost-action">
                <FaInfoCircle aria-hidden="true" />
                Scheda prodotto
              </Link>
            )}
            <button type="button" className="store-page__ghost-action" onClick={fetchProducts}>
              <FaRedo aria-hidden="true" />
              Aggiorna catalogo
            </button>
          </div>
        </div>
        <div className="store-page__trust-strip" aria-label="Garanzie store">
          <span>
            <FaShieldAlt aria-hidden="true" />
            Pagamento sicuro con Stripe
          </span>
          <span>Licenza account</span>
          <span>Launcher Windows</span>
        </div>
      </div>

      <div className="store-page__body">
        <div className="store-page__sync-row">
          <span className={`store-page__sync store-page__sync--${syncState}`}>
            {loading ? "Sincronizzo store..." : syncState === "live" ? "Store live" : syncState === "limited" ? "Store online - checkout non attivo" : "Catalogo base"}
          </span>
          {activeSession ? (
            <span className="store-page__account-state">Account collegato</span>
          ) : (
            <Link to="/account" className="store-page__account-state store-page__account-state--link">
              Accedi per acquistare
            </Link>
          )}
        </div>

        {notice && <div className="store-page__notice">{notice}</div>}
        {error && <div className="store-page__error">{error}</div>}

        <div className="store-page__grid" aria-busy={loading}>
          {(loading && !products.length ? FALLBACK_PRODUCTS : products).map((product) => {
            const media = getProductMedia(product);
            const purchaseType = getPurchaseType(product);
            const priceDisplay = getPriceDisplay(product);
            return (
              <article className="store-product" key={product.code}>
                <div className="store-product__media">
                  {media.hero ? <img src={media.hero} alt="" /> : null}
                  {media.logo ? (
                    <div className="store-product__media-overlay">
                      <img src={media.logo} alt={product.display_name} className="store-product__logo" />
                    </div>
                  ) : null}
                </div>
                <div className="store-product__content">
                  <div>
                    <span className="store-product__status">{getProductStatusLabel(product)}</span>
                    <h2>{product.display_name}</h2>
                    <ProductRating
                      compact
                      productCode={product.code}
                      productName={product.display_name}
                      rating={product}
                    />
                    <p>
                      {product.code === "warfront"
                        ? "FPS tattico per Windows. Leggi requisiti, licenza e supporto prima del checkout."
                        : product.code === "zombie-crushers"
                          ? "Survival arcade gratuito. Aggiungilo alla libreria e installalo dal launcher."
                          : "Titolo non ancora disponibile nello Store."}
                    </p>
                  </div>
                  <div className="store-product__meta">
                    <span>{priceDisplay || (purchaseType === "paid" ? "A pagamento" : purchaseType === "free" ? "Gratis" : "Non pronto")}</span>
                    <span>Windows</span>
                    <span>{product.installable ? "Installabile" : "Non installabile"}</span>
                  </div>
                  <div className="store-product__actions">
                    <Link to={getProductRoute(product)} className="store-page__secondary-action">
                      <FaInfoCircle aria-hidden="true" />
                      Scheda
                    </Link>
                    {renderProductAction(product)}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
